import React from 'react';
import { cn } from './cn';

export interface PageHeaderProps {
  /** Small uppercase label above the title (e.g. property address, inspection id). */
  eyebrow?: React.ReactNode;
  /** Main page title — rendered with the display font. */
  title: React.ReactNode;
  /** Optional subtitle or descriptor. */
  subtitle?: React.ReactNode;
  /** Leading slot — typically a back button or status icon. */
  leading?: React.ReactNode;
  /** Trailing slot — action buttons / status meta. */
  actions?: React.ReactNode;
  /** Full-width content below the main row (e.g. a ProgressBar or tab strip). */
  below?: React.ReactNode;
  className?: string;
}

export const PageHeader: React.FC<PageHeaderProps> = ({
  eyebrow, title, subtitle, leading, actions, below, className,
}) => (
  <header
    className={cn(
      'bg-surface-1 border-b border-border-subtle px-4 md:px-6 pt-4 pb-4 md:pt-6 md:pb-5',
      className,
    )}
  >
    <div className="flex items-start gap-3">
      {leading && <div className="flex-shrink-0 pt-0.5">{leading}</div>}
      <div className="flex-1 min-w-0">
        {eyebrow && (
          <div className="text-overline uppercase text-ink-subtle mb-1 truncate">{eyebrow}</div>
        )}
        <h1 className="font-display text-heading-lg md:text-display-sm text-ink leading-tight truncate">
          {title}
        </h1>
        {subtitle && (
          <div className="mt-1 text-body-sm text-ink-muted">{subtitle}</div>
        )}
      </div>
      {actions && (
        <div className="flex items-center gap-2 flex-shrink-0 pt-0.5">{actions}</div>
      )}
    </div>
    {below && <div className="mt-4">{below}</div>}
  </header>
);
